import $ from 'jquery';
import { View } from 'backbone';
import ko from 'knockout';
import _ from 'underscore';
import { decorate } from '@bingads-webui/dialog';
import { withUiBlock } from '@bingads-webui/uiblocker';
import { queryify } from '@bingads-webui/url-util';
import template from '../../template/product-groups-selector.pug';
import ViewModel from '../viewmodel/product-groups-selector';
import { getDoubleListBoxConfig, resetSort } from './double-list-box-config';

const NUMBER_OF_PARTITIONS_TO_SHOW = 1000;
const NO_DATA = '-';
const DimensionProperties = [
  'CategoryL1', 'CategoryL2', 'CategoryL3', 'CategoryL4', 'CategoryL5', 'Condition',
];

const ProductGroupSubTypeEnum = {
  standard: 1,
  coop: 2,
  all: 3,
};
const CampaignSubTypeEnum = {
  ShoppingCoOperative: 10,
  ShoppingSmartAds: 12,
};

class ProductGroupsSelectorView extends View {
  initialize({
    deps,
    currentPartition,
    displayName,
    children = [],
    partitionFullPath,
    defaultBid,
    campaignSubType,
    isBidValueOption = true,
  } = {}) {
    this.deps = deps;
    this.currentPartition = currentPartition;
    this.displayName = displayName;
    this.children = children;
    this.partitionFullPath = partitionFullPath;
    this.defaultBid = defaultBid;
    this.campaignSubType = campaignSubType;
    this.isBidValueOption = isBidValueOption;

    this.viewModel = new ViewModel({
      deps,
      currentPartitionName: displayName,
    });
    this.isCPGForBSCEnabled = Boolean(_.result(this.deps.config.get('Permissions'), 'IsCPGForBSCEnabled', false));
    this.isLocalInventoryAdV2Enabled = Boolean(_.result(this.deps.config.get('Permissions'), 'IsLocalInventoryAdV2Enabled', false));
  }

  render() {
    $.advertiser.ajaxPost(this.ajaxOptions)
      .then((dimensions) => {
        const productOptions = _.filter(dimensions, (productCondition) => {
          if (!this.isLocalInventoryAdV2Enabled && (productCondition.Id === 'Channel' || productCondition.Id === 'ChannelExclusivity')) {
            return false;
          }
          if (this.isCPGForBSCEnabled &&
            this.campaignSubType === CampaignSubTypeEnum.ShoppingCoOperative) {
            return productCondition.SubTypeFlag === ProductGroupSubTypeEnum.coop ||
              productCondition.SubTypeFlag === ProductGroupSubTypeEnum.all;
          }
          return productCondition.SubTypeFlag !== ProductGroupSubTypeEnum.coop;
        });

        this.$el.html(template({
          i18n: this.deps.i18n,
          i18nGlobal: this.deps.i18nGlobal,
        }));

        // fill available dimension data to select box
        this.viewModel.updateModel(productOptions);
        ko.applyBindings(this.viewModel, this.el);
        // render empty double list box
        const doubleListBoxConfig = getDoubleListBoxConfig({ deps: this.deps, view: this });
        doubleListBoxConfig.isBulkOperationEnabled = true;
        this.doubleListBox = new this.deps.DoubleListBox(doubleListBoxConfig).render();

        // set default value to select box,
        // it will trigger changeDimension function to update double list box
        this.viewModel.selectedDimension.subscribe(this.changeDimension, this);
        const selectedDimension = this.getSelectedDimension(productOptions);
        this.viewModel.selectedDimensionId(selectedDimension.Id);
        const selectedDimensionIndex = _.findIndex(dimensions, dim =>
          dim.Id === selectedDimension.Id);
        if (selectedDimensionIndex === 0) {
          this.changeDimension();
        }
      });
    return this;
  }

  get ajaxOptions() {
    const dataParam = {};

    _.each(this.partitionFullPath, (item, index) => {
      dataParam[`PathToParent[${index}].Attribute`] = item.Attribute;
      dataParam[`PathToParent[${index}].Operand`] = item.Operand;
    });

    const url = queryify('/campaign/ProductGroups/GetProductDimensions', {
      cid: this.deps.state.get('customer_id'),
      uid: this.deps.state.get('user_id'),
      aid: this.deps.state.get('account_id'),
      cmpid: this.deps.state.get('campaign_id'),
      adgid: this.deps.state.get('adgroup_id'),
      __adcenterAJAX: true,
    });

    return {
      url,
      data: dataParam,
      onAjaxComplete: (xhr) => {
        this.viewModel.showLimitExceededWarning(xhr.getResponseHeader('OfferLimitExceeded'));
      },
      onJsonError: ({ Errors } = {}) => {
        this.viewModel.errors(_.pluck(Errors, 'Message'));
      },
    };
  }

  getPartitionData(selectedDimension) {
    const url = `/Customers()/Accounts()/Campaigns(${this.deps.state.get('campaign_id')})/AdGroups(${this.deps.state.get('adgroup_id')})/ProductGroups(${_.result(this.currentPartition, 'Id')})/ProductAttributeStatistics/Default.GridData?$top=${NUMBER_OF_PARTITIONS_TO_SHOW}`;

    return this.deps.odata.post(url, {
      path: 'Advertiser/V2',
      data: {
        gridDataParameters: {
          DateRange: {
            DateRangePreset: null,
            StartDate: this.formatLocalDateToISO(this.deps.state.get('$daterange').start),
            EndDate: this.formatLocalDateToISO(this.deps.state.get('$daterange').end),
          },
        },
        operand: selectedDimension.Id,
      },
    });
  }

  formatLocalDateToISO(date) {
    return this.deps.i18n.formatDate(date, { timeZone: null, raw: 'yyyy-MM-dd' });
  }

  getSelectedDimension(data) {
    let selectedDimensionName = '';

    if (this.children.length) {
      selectedDimensionName = this.children[0].Condition.Operand;
    } else {
      // get default selected dimension according to given priority order
      const Ids = _.pluck(data, 'Id');

      selectedDimensionName = _.find(
        DimensionProperties,
        dimension => _.contains(Ids, dimension)
      ) || Ids[0];
    }

    // get selected dimension data using selectedDimensionName
    const result = _.find(data, { Id: selectedDimensionName });

    // fill selected items for double list box using current children nodes
    result.SelectedItems = _.chain(this.children)
      .map((child) => {
        if (child.Condition.Attribute) {
          return {
            Name: selectedDimensionName === 'Condition' ? this.deps.i18nGlobal.getString(`ProductCondition_Condition_${child.Condition.Attribute.toUpperCase()}`) : child.Condition.Attribute,
            Id: child.Condition.Attribute,
          };
        }
        return null;
      })
      .filter(Boolean)
      .value();
    return result;
  }

  changeDimension() {
    const newDimension = this.viewModel.selectedDimension();

    // should fetch products count data for partitions
    this.getPartitionData(newDimension)
      .then((data) => {
        // fill product count data to dimension data
        this.updateDimensionCount(newDimension, data.value);

        this.hideBulkPanel();
        // need to reset sort before source refreshed
        resetSort(this);
        this.doubleListBox.refreshSource();
        this.doubleListBox.setSelection(newDimension.SelectedItems);
      })
      .catch(({ errors } = {}) => {
        this.viewModel.errors.add(_.isEmpty(errors) ? undefined : this.deps.getODataErrors(errors));
      })
      .finally(withUiBlock(this.$el.parent(), this.deps.i18nGlobal.getString('loadingtext')));
  }

  updateDimensionCount(selectedDimension, counts) {
    if (selectedDimension.IsBulkEnabled) {
      /* eslint-disable no-param-reassign */
      selectedDimension.AvailableItems = _.map(counts, count =>
        ({
          Id: count.AttributeName,
          Name: count.AttributeName,
          Count: count.OfferCount ? count.OfferCount : NO_DATA,
          Clicks: count.PerformanceMetrics.Clicks ?
            count.PerformanceMetrics.Clicks : NO_DATA,
          Impressions: count.PerformanceMetrics.Impressions ?
            count.PerformanceMetrics.Impressions : NO_DATA,
          Conversions: count.PerformanceMetrics.Conversions ?
            count.PerformanceMetrics.Conversions : NO_DATA,
        }));
    } else {
      _.each(selectedDimension.AvailableItems, (item) => {
        const dimension = _.find(counts, count => item.Name && count.AttributeName &&
          item.Name.toLowerCase() === count.AttributeName.toLowerCase());

        item.Count = dimension && dimension.OfferCount ? dimension.OfferCount : NO_DATA;
        item.Clicks = dimension && dimension.PerformanceMetrics.Clicks ?
          dimension.PerformanceMetrics.Clicks : NO_DATA;
        item.Impressions = dimension && dimension.PerformanceMetrics.Impressions ?
          dimension.PerformanceMetrics.Impressions : NO_DATA;
        item.Conversions = dimension && dimension.PerformanceMetrics.Conversions ?
          dimension.PerformanceMetrics.Conversions : NO_DATA;
      });
    }

    _.each(selectedDimension.SelectedItems, (item) => {
      const dimension = _.find(counts, count => item.Name && count.AttributeName &&
        item.Name.toLowerCase() === count.AttributeName.toLowerCase());

      item.Count = dimension ? dimension.OfferCount : NO_DATA;
    });
  }

  get events() {
    return {
      'click .save-button': 'saveProductGroups',
      'click .cancel-button': 'remove',
      'click .showBulkPanel': 'showBulkPanel',
      'click .hideBulkPanel': 'hideBulkPanel',
      'click .add-custom-button': 'addBulkValues',
      'click .gotoCampaignSettings': 'navigateToCampaignSettingsTab',
    };
  }

  showBulkPanel() {
    this.viewModel.showBulkPanel(true);
  }

  hideBulkPanel() {
    this.viewModel.showBulkPanel(false);
  }

  addBulkValues() {
    /* eslint-disable no-underscore-dangle */
    let textValue = this.doubleListBox._viewModel.customData.textAreaValue();

    if (!textValue || !textValue.trim()) {
      return;
    }
    textValue = textValue.trim();
    const bulkInput = _.uniq(
      _.map(textValue.split('\n'), text => text.trim()),
      val => val.toLowerCase()
    );
    const selectedDimension = this.viewModel.selectedDimension();
    const selectedItems = _.clone(this.doubleListBox._viewModel.selectedItems());
    const addedPartitions = [];

    _.each(bulkInput, (input) => {
      if (_.find(selectedItems, item => item.Name.toLowerCase() === input.toLowerCase())) {
        return;
      }
      addedPartitions.push({
        Name: input,
        Id: input,
        Count: _.result(_.find(
          selectedDimension.AvailableItems,
          item => item.Name.toLowerCase() === input.toLowerCase()
        ), 'Count', 0),
      });
    });

    this.doubleListBox._viewModel.customData.textAreaValue('');
    selectedItems.push(...addedPartitions);
    this.doubleListBox.setSelection(selectedItems);
  }

  navigateToCampaignSettingsTab() {
    this.remove();
    window.App.navigate('customer/:customer_id/account/:account_id/campaign/:campaign_id/edit');
  }

  saveProductGroups() {
    const selectedDimension = this.viewModel.selectedDimension();
    const newPartitions = _.pluck(this.doubleListBox._viewModel.selectedItems(), 'Id');
    const childrenAttribute = _.chain(this.children)
      .pluck('Condition')
      .pluck('Attribute')
      .filter(Boolean)
      .value() || [];

    if ((this.currentPartition.IsLeaf && !newPartitions.length) ||
      _.isEqual(newPartitions.sort(), childrenAttribute.sort())) {
      this.remove();
      return;
    }

    const changeMode = this.getChangeMode(selectedDimension.Id, newPartitions);

    if (changeMode === 3) {
      const message = this.isBidValueOption ?
        this.deps.i18n.getString('ProductGroupsGrid_ChangeOperandWarning').replace('{0}', this.displayName).replace('{1}', this.defaultBid) :
        this.deps.i18n.getString('ProductGroupsGrid_ChangeOperandWarning_BidBoost', {
          productPartitionName: this.displayName,
          adgroupBidBoost: this.defaultBid,
        });

      if (!this.deps.popup.confirm(message)) {
        return;
      }
    }

    const params =
      this.getUpdateParams(newPartitions, childrenAttribute, selectedDimension.Id, changeMode);

    if (this.checkIfExceedMaxNumberOfForm(params)) {
      this.updateSubdivisions(params, changeMode);
    }
  }

  updateSubdivisions(params, changeMode) {
    const url = queryify('/campaign/ProductGroups/UpdateSubdivision', {
      cid: this.deps.state.get('customer_id'),
      uid: this.deps.state.get('user_id'),
      aid: this.deps.state.get('account_id'),
      cmpid: this.deps.state.get('campaign_id'),
      adgid: this.deps.state.get('adgroup_id'),
      __adcenterAJAX: true,
    });

    $.advertiser.ajaxPost({
      url,
      data: params,
      onJsonError: ({ Errors } = {}) => {
        this.viewModel.errors(_.pluck(Errors, 'Message'));
      },
    }).then((data) => {
      if (!_.result(data, 'Errors')) {
        this.trigger('save', {
          expandedNodeDelta: changeMode === 0 ? _.first(data.CreatedPartitionIds) : undefined,
        });
      }
    });
  }

  checkIfExceedMaxNumberOfForm(data) {
    if (_.size(data) > Number(this.deps.config.get('MaxHttpCollectionKeys'))) {
      this.viewModel.errors([this.deps.i18n.getString('ProductGroupsGrid_Error_TooManyProductGroups')]);
      return false;
    }

    return true;
  }

  getUpdateParams(newPartitions, oldPartitions, dimensionType, changeMode) {
    const newPartitionsLowerCaseCopy = _.map(
      newPartitions || [],
      item => String(item).toLowerCase()
    );
    const oldPartitionsLowerCaseCopy = _.map(
      oldPartitions || [],
      item => String(item).toLowerCase()
    );
    const newBid = this.getNewBid(changeMode);
    const params = {};
    const added = [];
    const deleted = [];
    const parentId = this.currentPartition.ParentId > 0 ? this.currentPartition.ParentId : null;

    switch (changeMode) {
    // add partition under leaf
      case 0:
        // delete parent as leaf
        deleted.push(this.currentPartition.Id);
        // add parent as subdivision
        added.push(this.createProductGroup(
          this.currentPartition.Condition.Operand,
          this.currentPartition.Condition.Attribute, -1, parentId, null
        ));
        // add new partitions
        _.each(newPartitions, (item) => {
          added.push(this.createProductGroup(dimensionType, item, null, -1, newBid));
        });
        // add everything else node
        added.push(this.createProductGroup(dimensionType, '', null, -1, newBid));
        break;
      // delete all partitions under subdivision
      case 1:
        // delete all partitions(including everything else)
        deleted.push(..._.pluck(this.children, 'Id'));
        // delete parent as subdivision
        deleted.push(this.currentPartition.Id);
        // add parent as leaf
        added.push(this.createProductGroup(
          this.currentPartition.Condition.Operand,
          this.currentPartition.Condition.Attribute, null, parentId, newBid
        ));
        break;
      // add or delete partition under subdivision in same dimension
      case 2:
        // delete partitions which doesn't exist in new partition list(except everything else)
        _.each(this.children, (child) => {
          if (!_.isEmpty(child.Condition.Attribute) &&
            !_.contains(newPartitionsLowerCaseCopy, child.Condition.Attribute.toLowerCase())) {
            deleted.push(child.Id);
          }
        });
        // add partitions which doesn't exist in current children list
        _.each(newPartitions, (item) => {
          if (!_.contains(oldPartitionsLowerCaseCopy, String(item).toLowerCase())) {
            added.push(this.createProductGroup(
              dimensionType,
              item, null, this.currentPartition.Id, newBid
            ));
          }
        });
        break;
      // add partition under subdivision in different dimension
      case 3:
        // delete all old partitions(including everything else)
        deleted.push(..._.pluck(this.children, 'Id'));
        // add new partitions
        _.each(newPartitions, (item) => {
          added.push(this.createProductGroup(
            dimensionType,
            item, null, this.currentPartition.Id, newBid
          ));
        });
        // add everything else
        added.push(this.createProductGroup(dimensionType, '', null, this.currentPartition.Id, newBid));
        break;
      default:
        break;
    }

    _.each(added, (item, index) => {
      _.each(_.keys(item), (key) => {
        params[`Added[${index}].${key}`] = item[key];
      });
    });
    _.each(deleted, (item, index) => {
      params[`Deleted[${index}]`] = item;
    });
    return params;
  }

  getChangeMode(newDimension, newPartitions) {
    if (this.currentPartition.IsLeaf && !this.children.length) {
      // add new partitions under leaf
      return 0;
    }
    if (!newPartitions.length) {
      // delete all partitions under subdivision
      return 1;
    }
    if (this.children.length && this.children[0].Condition.Operand === newDimension) {
      // add or delete partitions in same dimension
      return 2;
    }
    // add partition in different dimensions
    return 3;
  }

  getNewBid(changeMode) {
    let inheritNode = null;

    switch (changeMode) {
      case 0:
        // add patition under leaf node, new partition inherits bid from leaf node.
        inheritNode = this.currentPartition;
        break;
      case 2:
        // add partition under non-leaf node,
        // new partition inherits bid from everythingElse at the same level.
        inheritNode = _.find(this.children, child => _.isEmpty(child.Condition.Attribute));
        break;
      default:
        // inherit bid from ad group
        break;
    }
    if (_.result(inheritNode, 'IsExcluded') === true) {
      return '';
    }

    if (this.isBidValueOption) {
      const bid = _.result(_.result(_.result(inheritNode, 'BidAmount'), 'Bid'), 'Amount');

      return { BidValue: bid ? this.deps.decimal.fromFloatToString(bid, 2) : this.defaultBid };
    }

    const bidBoost = _.result(_.result(inheritNode, 'BidBoost'), 'Multiplier');

    return {
      BidBoost: bidBoost ? this.deps.decimal.fromFloatToString(bidBoost, 2) : this.defaultBid,
    };
  }

  createProductGroup(dimensionType, dimensionValue, Id, parentId, bid) {
    return _.extend({
      DimensionType: dimensionType,
      DimensionValue: dimensionValue,
      Id,
      ParentId: parentId,
    }, bid);
  }
}

export const ProductGroupsSelectorDialog = decorate(ProductGroupsSelectorView, {
  maxWidth: 1500,
  width: 1100,
  minHeight: 530,
  draggable: false,
  position: 'center',
});
